import { useEffect } from "react";
import { Badge, Button, Card } from "flowbite-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaChartLine, FaGift, FaTrophy } from "react-icons/fa";
import CelebrationBadge from "./CelebrationBadge";
import NextStepCard from "./NextStepCard";
import { launchCelebrationConfetti } from "../../utils/confetti";

const proximosPassos = [
  {
    title: "Organize sua vitrine",
    description:
      "Cadastre sua loja, ajuste sua identidade e deixe tudo pronto para transmitir mais confiança desde o primeiro clique.",
  },
  {
    title: "Publique seus produtos",
    description:
      "Suba fotos, organize tags e facilite o atendimento com uma vitrine clara, bonita e rápida de navegar.",
  },
  {
    title: "Transforme interesse em conversa",
    description:
      "Use sua Vintree para acelerar respostas no WhatsApp e converter visitas em clientes com muito menos atrito.",
  },
];

const ContaAtivada = () => {
  const navigate = useNavigate();
  const profile = useSelector((state) => state.user?.profile || null);
  const loja = useSelector((state) => state.user?.loja || null);

  const nomeLoja = (loja?.nome_fantasia || profile?.nome || "sua loja").trim();

  useEffect(() => {
    launchCelebrationConfetti({
      particleCount: 140,
      spread: 90,
      origin: { y: 0.55 },
    });

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-18121594637/8uhBCP7o58UcEI2uhsFD",
      });
    }
  }, []);

  return (
    <div className="relative overflow-hidden px-4 py-8 sm:px-6 lg:px-10">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl"
        aria-hidden="true"
      >
        <div
          className="relative left-1/2 aspect-[1155/678] w-[38rem] -translate-x-1/2 rotate-[18deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:w-[72rem]"
          style={{
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
        />
      </div>

      <Card className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] border-0 bg-gradient-to-r from-[#fff1be] via-[#ee87cb] to-[#b060ff] p-0 shadow-2xl">
        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[1.2fr_0.8fr] lg:p-10">
          <div className="space-y-6">
            <CelebrationBadge>Conta ativada com sucesso</CelebrationBadge>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Parabéns! Você acabou de fazer uma ótima escolha para transformar a {nomeLoja}.
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-gray-900/85">
                Sua Vintree agora está pronta para deixar seu atendimento mais rápido,
                organizado e convincente. É a confirmação de uma decisão inteligente:
                investir em uma experiência que valoriza seus produtos e facilita a venda.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Badge color="purple" size="sm">
                Escolha confirmada
              </Badge>
              <Badge color="pink" size="sm">
                Atendimento mais ágil
              </Badge>
              <Badge color="success" size="sm">
                Loja mais profissional
              </Badge>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                pill
                onClick={() => navigate("/app/home")}
                className="bg-black text-white hover:bg-gray-800"
              >
                Ir para a Home
              </Button>
              <Button
                size="lg"
                pill
                gradientDuoTone="pinkToOrange"
                onClick={() => navigate("/app/loja")}
              >
                Configurar minha loja
                <FaArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col justify-between gap-4">
            <Card className="rounded-3xl border-0 bg-white/90 text-center shadow-lg backdrop-blur">
              <button
                type="button"
                onClick={() => launchCelebrationConfetti({ particleCount: 160, spread: 100 })}
                className="mx-auto flex flex-col items-center gap-3"
              >
                <FaTrophy className="text-6xl text-yellow-500 animate-bounce" />
                <span className="text-sm font-medium text-gray-700">
                  Clique no troféu para comemorar de novo
                </span>
              </button>
            </Card>

            <Card className="rounded-3xl border-0 bg-white/90 shadow-lg backdrop-blur">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-purple-100 p-3 text-purple-700">
                    <FaChartLine className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Agora o próximo passo é simples</h2>
                    <p className="text-sm text-gray-700">
                      Complete sua estrutura e comece a colher resultado mais rápido.
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-200">
                  <div className="flex items-start gap-3">
                    <FaGift className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                      Você já deu o passo mais importante: decidiu profissionalizar sua loja.
                      Agora vamos transformar isso em percepção de valor para quem compra.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Card>

      <div className="mx-auto mt-8 grid max-w-5xl gap-4 md:grid-cols-3">
        {proximosPassos.map((passo) => (
          <NextStepCard key={passo.title} title={passo.title} description={passo.description} />
        ))}
      </div>
    </div>
  );
};

export default ContaAtivada;
